import { landingApiRequest } from '@/lib/landing-api'
import { env } from '@/lib/env'

const STORAGE_PREFIX = 'sortee:donor-sso:'
const MAX_AGE_MS = 10 * 60 * 1000
const OPAQUE_VALUE = /^[A-Za-z0-9_-]{32,256}$/
const SSO_ERROR = 'Não foi possível confirmar seu acesso. Entre novamente para continuar.'

type PendingSso = {
  verifier: string
  redirectUri: string
  createdAt: number
  returnQuery: string
}

function safeUrl(value: string): URL {
  const url = new URL(value)
  const loopback = ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname)
  if (
    url.username ||
    url.password ||
    url.hash ||
    (url.protocol !== 'https:' && !(loopback && url.protocol === 'http:'))
  ) {
    throw new Error(SSO_ERROR)
  }
  return url
}

function base64Url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

export function canStartDonorSso(): boolean {
  try {
    return (
      window.top === window &&
      Boolean(env.publicAppUrl) &&
      safeUrl(env.publicAppUrl).origin !== window.location.origin
    )
  } catch {
    return false
  }
}

export function canAuthorizeDonorSso(): boolean {
  try {
    return (
      window.top === window &&
      window.location.pathname === '/conta' &&
      safeUrl(env.publicAppUrl).origin === window.location.origin
    )
  } catch {
    return false
  }
}

export async function startDonorSso(
  campaignId: number,
  slug: string,
  searchParams: URLSearchParams
): Promise<string> {
  const central = safeUrl(env.publicAppUrl)
  const redirectUri = `${window.location.origin}/campanha/${encodeURIComponent(slug)}/conta`
  const verifier = base64Url(crypto.getRandomValues(new Uint8Array(32)))
  const state = base64Url(crypto.getRandomValues(new Uint8Array(32)))
  const challenge = base64Url(
    new Uint8Array(
      await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
    )
  )
  const params = new URLSearchParams()
  for (const key of ['af', 'quantity'] as const) {
    const value = searchParams.get(key)?.trim()
    if (value) params.set(key, value)
  }
  const pending: PendingSso = {
    verifier,
    redirectUri,
    createdAt: Date.now(),
    returnQuery: params.toString(),
  }
  // The verifier is a short-lived proof for this tab; the authenticated session stays in httpOnly cookies.
  sessionStorage.setItem(`${STORAGE_PREFIX}${state}`, JSON.stringify(pending))
  try {
    const response = await landingApiRequest<{ authorize_url: string }>(
      '/api/donor/sso/start',
      {
        method: 'POST',
        body: JSON.stringify({
          campaign_id: campaignId,
          code_challenge: challenge,
          redirect_uri: redirectUri,
          state,
        }),
      }
    )
    const destination = safeUrl(response.authorize_url)
    if (
      destination.origin !== central.origin ||
      destination.pathname !== '/conta' ||
      !OPAQUE_VALUE.test(destination.searchParams.get('sso_request') ?? '')
    ) {
      throw new Error(SSO_ERROR)
    }
    return destination.toString()
  } catch (error) {
    sessionStorage.removeItem(`${STORAGE_PREFIX}${state}`)
    throw error
  }
}

export type DonorSsoPreview = {
  redirect_origin: string
  campaign_name: string
  requires_confirmation: boolean
}

export async function previewDonorSso(requestToken: string): Promise<DonorSsoPreview> {
  if (!OPAQUE_VALUE.test(requestToken)) throw new Error(SSO_ERROR)
  const preview = await landingApiRequest<DonorSsoPreview>('/api/donor/sso/preview', {
    method: 'POST',
    body: JSON.stringify({ request_token: requestToken }),
  })
  if (
    safeUrl(preview.redirect_origin).origin !== preview.redirect_origin ||
    typeof preview.campaign_name !== 'string' ||
    typeof preview.requires_confirmation !== 'boolean'
  ) {
    throw new Error(SSO_ERROR)
  }
  return preview
}

export async function authorizeDonorSso(
  requestToken: string,
  confirmed = false
): Promise<string> {
  if (!OPAQUE_VALUE.test(requestToken)) throw new Error(SSO_ERROR)
  const response = await landingApiRequest<{ redirect_uri: string }>(
    '/api/donor/sso/authorize',
    {
      method: 'POST',
      body: JSON.stringify({
        request_token: requestToken,
        ...(confirmed ? { confirmed: true } : {}),
      }),
    }
  )
  const destination = safeUrl(response.redirect_uri)
  if (
    !/^\/campanha\/[^/]+\/conta$/.test(destination.pathname) ||
    !OPAQUE_VALUE.test(destination.searchParams.get('sso_code') ?? '') ||
    !OPAQUE_VALUE.test(destination.searchParams.get('sso_state') ?? '')
  ) {
    throw new Error(SSO_ERROR)
  }
  return destination.toString()
}

export async function exchangeDonorSso(
  code: string,
  state: string,
  slug: string
): Promise<string> {
  if (!OPAQUE_VALUE.test(code) || !OPAQUE_VALUE.test(state)) throw new Error(SSO_ERROR)
  const key = `${STORAGE_PREFIX}${state}`
  const raw = sessionStorage.getItem(key)
  sessionStorage.removeItem(key)
  if (!raw) throw new Error(SSO_ERROR)
  const pending = JSON.parse(raw) as Partial<PendingSso>
  const redirectUri = `${window.location.origin}/campanha/${encodeURIComponent(slug)}/conta`
  if (
    typeof pending.verifier !== 'string' ||
    !OPAQUE_VALUE.test(pending.verifier) ||
    pending.redirectUri !== redirectUri ||
    typeof pending.createdAt !== 'number' ||
    Date.now() - pending.createdAt > MAX_AGE_MS ||
    pending.createdAt > Date.now() ||
    typeof pending.returnQuery !== 'string'
  ) {
    throw new Error(SSO_ERROR)
  }
  await landingApiRequest('/api/donor/sso/exchange', {
    method: 'POST',
    body: JSON.stringify({
      code,
      code_verifier: pending.verifier,
      redirect_uri: redirectUri,
    }),
  })
  const params = new URLSearchParams(pending.returnQuery)
  const safeParams = new URLSearchParams()
  for (const key of ['af', 'quantity']) {
    const value = params.get(key)
    if (value) safeParams.set(key, value)
  }
  const query = safeParams.toString()
  return `/campanha/${encodeURIComponent(slug)}/perfil${query ? `?${query}` : ''}`
}

export function stripDonorSsoCallbackParams(): void {
  const url = new URL(window.location.href)
  url.searchParams.delete('sso_code')
  url.searchParams.delete('sso_state')
  window.history.replaceState(
    window.history.state,
    '',
    `${url.pathname}${url.search}${url.hash}`
  )
}
